/**
 * Analytics + consent.
 *
 * GA4 runs under Google Consent Mode v2. index.html sets the default consent
 * state to denied (globally) before `gtag('config', ...)`. Nothing is tracked
 * until the visitor accepts via the consent banner; on acceptance we update
 * consent to granted and start sending page_view events.
 *
 * Scope decision: consent is required for ALL visitors (no region exception).
 */

export type Consent = 'granted' | 'denied'

const STORAGE_KEY = 'cookie-consent'

let consentGranted = false

function gtag(...args: unknown[]): void {
  const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag
  if (typeof g === 'function') g(...args)
}

/** The visitor's stored choice, or null if they haven't chosen yet. */
export function getStoredConsent(): Consent | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch { return null }
}

/** Re-apply a previously stored choice on page load (call before router.init). */
export function applyStoredConsent(): void {
  if (getStoredConsent() === 'granted') {
    consentGranted = true
    gtag('consent', 'update', { analytics_storage: 'granted' })
  }
}

/** Record a choice made via the banner (persist + update GA + maybe track). */
export function setConsent(choice: Consent): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, choice)
  } catch { /* The choice remains effective for this page even without storage. */ }
  consentGranted = choice === 'granted'
  gtag('consent', 'update', { analytics_storage: choice })
  // Count the visit on which consent was granted (the initial page_view was
  // suppressed because consent had not been given yet).
  if (consentGranted) trackPageView()
}

/** Clear the stored choice so the banner can be shown again. */
export function resetConsent(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY)
  } catch { /* Storage may be unavailable. */ }
}

/**
 * Send a GA4 page_view for the current document. No-op until the visitor has
 * granted analytics consent. Called by the router on every navigation.
 */
export function trackPageView(): void {
  if (!consentGranted) return
  gtag('event', 'page_view', {
    page_location: window.location.href,
    page_title: document.title,
  })
}

/**
 * Send an arbitrary GA4 event (e.g. generate_lead, language_switch). No-op
 * until the visitor has granted analytics consent, so it is safe to call from
 * anywhere without re-checking consent. Keep event names snake_case and reuse
 * GA4's recommended names where they exist.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!consentGranted) return
  gtag('event', name, params)
}
